import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, can, invitableRolesFor, type Role } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { Field, field } from '../create/Field';
import { IconTeam, IconTrash } from '../icons';
import { CopyButton } from '../ui/CopyButton';
import {
  CUSTOMER_EMAIL_POLL_MS,
  CustomerEmailLine,
  customerEmailPending,
  type CustomerEmailView,
} from '../ui/CustomerEmailLine';

/**
 * Who works here.
 *
 * Until this shipped, a shop was whoever signed up — one person, permanently. There was no
 * procedure anywhere that could add a second, so the owner + office + techs the product is
 * designed around could only be assembled by hand-editing the database.
 *
 * Invites hand back a LINK, not just a promise of an email. Email delivery depends on a
 * vendor key that may not be set, and hiring must not. The owner can read the link to the
 * new tech standing in front of them; if the email also went out, the card says so — and if
 * it didn't, it says that too rather than implying something is in flight.
 *
 * Since MAP 249 the email is QUEUED, and the outbox decides when it goes: it may WAIT (an
 * unconfirmed owner, a hold, a daily limit). So what the card says of it is the server's word
 * for that invite's email (`team.list`'s `emailState`), in the words a detail page's email line
 * uses, never a sentence of its own (QA D7: "We're emailing them this link" while it waited).
 */

/** The invite just made, shown until they navigate away (or press Done). */
export interface FreshInvite {
  id: string;
  url: string;
  email: string;
  /** When its answer came (ms): a list read after it is one that knows about it. */
  at: number;
  /** What the invite's own answer said of its email: on its way, or never going and why. */
  answered: CustomerEmailView;
}

/** The team list as the card last read it: its invites, and when that answer came (ms). */
export interface TeamListRead {
  invites: readonly { id: string; emailState: CustomerEmailView | null }[] | undefined;
  updatedAt: number;
}

/**
 * What became of the fresh invite's email, as the server last said it: its row in the team list
 * (`emailState`, read from the outbox); until a list read after the invite has it, the invite's
 * own answer. Null once such a read no longer lists it: it is not waiting to join any more
 * (accepted, or cancelled), so its link and its email are nothing to show.
 */
export function freshInviteEmail(
  fresh: FreshInvite,
  list: TeamListRead,
): CustomerEmailView | null {
  const row = list.invites?.find((i) => i.id === fresh.id);
  if (row) return row.emailState ?? fresh.answered;
  return list.updatedAt > fresh.at ? null : fresh.answered;
}

/**
 * How often the card asks again: while the fresh invite's email is still on its way (queued and
 * not waiting, or sending), as a detail page asks after its own email; never otherwise.
 */
export function teamListPoll(
  fresh: FreshInvite | null,
  list: TeamListRead,
): number | false {
  return fresh && customerEmailPending(freshInviteEmail(fresh, list))
    ? CUSTOMER_EMAIL_POLL_MS
    : false;
}

const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  office: 'Office',
  tech: 'Technician',
  bookkeeper: 'Bookkeeper',
};

const ROLE_HINT: Record<Role, string> = {
  owner: 'Everything, including billing and closing the account.',
  office: 'Dispatch, quotes, invoices and customers. No billing.',
  tech: 'Their own schedule and the work. No prices, no reports.',
  bookkeeper: 'Read-only on invoices, payments and reports, except revenue by person.',
};

export function TeamCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const me = useSession();
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('tech');
  /** The link from the invite we just made — shown until they navigate away. */
  const [fresh, setFresh] = useState<FreshInvite | null>(null);

  const q = useQuery({
    ...trpc.team.list.queryOptions(),
    refetchInterval: (query) =>
      teamListPoll(fresh, {
        invites: query.state.data?.invites,
        updatedAt: query.state.dataUpdatedAt,
      }),
  });
  const freshEmail = fresh
    ? freshInviteEmail(fresh, { invites: q.data?.invites, updatedAt: q.dataUpdatedAt })
    : null;
  const invalidate = () => void qc.invalidateQueries();

  const invite = useMutation(
    trpc.team.invite.mutationOptions({
      onSuccess: (r) => {
        // The email is QUEUED (MAP 249): the answer can only say whether it is on its way or
        // can never go (no address, email off), and why. Whether it then WAITS is the outbox's
        // to say, through the list (`freshInviteEmail`).
        setFresh({
          id: r.id,
          url: r.url,
          email: r.email,
          at: Date.now(),
          answered: {
            status: r.emailStatus,
            waitingFor: null,
            failure: r.emailFailure,
            toEmail: null,
          },
        });
        setInviting(false);
        setEmail('');
        invalidate();
      },
    }),
  );
  const revoke = useMutation(
    trpc.team.revokeInvite.mutationOptions({ onSuccess: invalidate }),
  );
  const changeRole = useMutation(
    trpc.team.changeRole.mutationOptions({ onSuccess: invalidate }),
  );
  const remove = useMutation(trpc.team.remove.mutationOptions({ onSuccess: invalidate }));

  const mayInvite = can(me.role, 'member', 'invite');
  const mayManage = can(me.role, 'member', 'update');
  const busyError =
    invite.error ?? revoke.error ?? changeRole.error ?? remove.error ?? null;

  const state = shownState(q, q.data);

  return (
    <SectionCard
      id={id}
      title={counted('Your team', state, q.data?.members.length ?? 0)}
      right={
        // Same rule: an invite adds a member the failed list would have shown.
        mayInvite && !inviting && state === 'ready' ? (
          <Button onClick={() => setInviting(true)}>Add someone</Button>
        ) : null
      }
    >
      {busyError ? <Notice tone="danger">{busyError.message}</Notice> : null}

      {fresh && freshEmail ? (
        <InviteLink
          url={fresh.url}
          email={fresh.email}
          emailState={freshEmail}
          onDone={() => setFresh(null)}
        />
      ) : null}

      {inviting ? (
        <div className="mb-3 space-y-3 rounded-[var(--radius)] border border-border p-3">
          <Field label="Their email">
            <input
              className={field}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoFocus
              placeholder="tech@yourshop.com"
            />
          </Field>
          <Field label="What they do here">
            <select
              className={field}
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {invitableRolesFor(me.role).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </Field>
          <p className="text-xs text-muted-foreground">{ROLE_HINT[role]}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={!email.trim() || invite.isPending}
              onClick={() => invite.mutate({ email: email.trim(), role })}
            >
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
            <Button variant="ghost" onClick={() => setInviting(false)}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        `bookkeeper` holds no `member` permission at all, so this query 403s for them and
        the old branch answered with "Something went wrong — try again" under a button that
        could only fail. The contract asks `isForbidden` for every card at once.
      */}
      {!q.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your team"
          icon={IconTeam}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : (
        <>
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          <ul className="divide-y divide-border">
            {q.data.members.map((m) => {
              const isMe = m.userId === me.userId;
              return (
                <li key={m.userId} className="flex flex-wrap items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {m.name || m.email}
                      {isMe ? (
                        <span className="ml-2 text-xs text-muted-foreground">you</span>
                      ) : null}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  {mayManage ? (
                    <select
                      className={`${field} w-auto min-w-[9rem]`}
                      value={m.role}
                      disabled={changeRole.isPending}
                      aria-label={`Role for ${m.name || m.email}`}
                      onChange={(e) =>
                        changeRole.mutate({
                          userId: m.userId,
                          role: e.target.value as Role,
                        })
                      }
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <StatusBadge tone="neutral">{ROLE_LABEL[m.role]}</StatusBadge>
                  )}
                  {mayManage && !isMe ? (
                    <Button
                      variant="ghost"
                      disabled={remove.isPending}
                      title={`Remove ${m.name || m.email} from this shop`}
                      onClick={() => {
                        // Irreversible for them (they lose access this second) and easy to
                        // mis-click next to a dropdown, so it asks. Their work stays.
                        if (
                          window.confirm(
                            `Remove ${m.name || m.email}? They lose access right away. The jobs and notes they've written stay.`,
                          )
                        ) {
                          remove.mutate({ userId: m.userId });
                        }
                      }}
                    >
                      <IconTrash size={14} />
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {q.data.invites.length > 0 ? (
            <div className="mt-4 border-t border-border pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Waiting to join ({q.data.invites.length})
              </p>
              <ul className="space-y-2">
                {q.data.invites.map((i) => (
                  <li
                    key={i.id}
                    className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-border p-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{i.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {ROLE_LABEL[i.role]} ·{' '}
                        {i.expired
                          ? 'link has expired'
                          : `expires ${i.expiresAt.toLocaleDateString()}`}
                      </p>
                    </div>
                    {i.expired ? (
                      <StatusBadge tone="warn">expired</StatusBadge>
                    ) : (
                      <CopyButton
                        value={i.url}
                        label="Copy link"
                        promptLabel="Copy this link"
                      />
                    )}
                    {mayInvite ? (
                      <Button
                        variant="ghost"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate({ inviteId: i.id })}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {q.data.members.length === 1 && q.data.invites.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              It's just you so far. Add your office manager and your techs — everyone gets
              their own login, and techs only see their own work.
            </p>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

/** The link is the product of an invite, so it gets its own block rather than a toast that
 *  vanishes before an owner can read a URL out loud. */
function InviteLink({
  url,
  email,
  emailState,
  onDone,
}: {
  url: string;
  email: string;
  emailState: CustomerEmailView;
  onDone: () => void;
}) {
  return (
    <div className="mb-3 space-y-2 rounded-[var(--radius)] border border-success bg-success/5 p-3">
      <p className="text-sm font-medium">Invite ready for {email}</p>
      {/* What became of its email, as the outbox says (QA D7): "Sending…", "Waiting for the
          owner to confirm their email address", "Sent to …", or "Didn't send: …" (only on the
          arm that KNOWS: `CustomerEmailLine`). Never "we're emailing them" while it waits: an
          owner who thinks a message went out will stand there waiting for a hire who never got
          one. */}
      <CustomerEmailLine email={emailState}>
        Pass this link on yourself.
      </CustomerEmailLine>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-[var(--radius)] border border-border bg-surface px-2 py-1.5 text-xs">
          {url}
        </code>
        <CopyButton value={url} label="Copy" promptLabel="Copy this link" />
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
